import * as React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import { CmdLoader, CmdSimpleSelect } from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { getWaypointFormData, normalizeOptions } from './WaypointService';
import {
  AreaTypes,
  IFormWaypointResponseData,
  IFiltersWaypoint,
  RefuelingType,
  translateOptions,
} from './types';
import PermissionsService, {
  PERMISSION_WAYPOINT_CUSTOMER,
  PERMISSION_WAYPOINT_PRIVATE,
} from '../../../Services/PermissionsService';
import { ISimpleSelectOption } from '../../Forms/ArrayFields/interfaces';
import SimpleSelectWithSearchHookForm from '../../Forms/Fields/SimpleSelectWithSearchHookForm';
import SimpleSelectWithIcon from '../../Forms/ReactHookFormFields/SimpleSelectWithIcon';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { activeFilterAtom, filterOptionsWpAtom } from './WaypointState';

interface IFilterWaypointsFormProps {
  closeFilterOptions: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  saveFilterWaypoint: (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

type FilterWaypointsFormValues = {
  icon: string;
  type: string;
  customer: string;
  areaType: AreaTypes | '';
  refuelingTypeList: RefuelingType | '';
};

function FilterWaypointsForm(props: IFilterWaypointsFormProps): JSX.Element {
  const intlShape = useIntl();
  const { formatMessage: f } = intlShape;
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [activeFilter, setActiveFilter] = useRecoilState(activeFilterAtom);
  const [formOptions, setFormOptions] = React.useState<IFormWaypointResponseData | null>(null);
  const setFilterOptions = useSetRecoilState(filterOptionsWpAtom);
  const [customerFormData, setCustomerFormData] = React.useState<ISimpleSelectOption[] | null>(
    null
  );

  const { handleSubmit, control, setValue, trigger, watch } = useForm<FilterWaypointsFormValues>({
    mode: 'onChange',
    defaultValues: {
      icon: '',
      type: '',
      customer: '',
      areaType: '',
      refuelingTypeList: undefined,
    },
  });

  const handleFormOptions = async () => {
    const response = await getWaypointFormData();

    if (response) {
      const formDataCustomer: ISimpleSelectOption[] = response.customerId.values.filter(
        (item) =>
          selectedCustomers.includes(Number(item.value)) &&
          (PermissionsService.hasAccess(
            PERMISSION_WAYPOINT_CUSTOMER,
            ['read'],
            Number(item.value)
          ) ||
            PermissionsService.hasAccess(PERMISSION_WAYPOINT_PRIVATE, ['read'], Number(item.value)))
      );

      if (response && response.customerId && activeFilter && activeFilter.customerId) {
        setValue('customer', String(activeFilter.customerId));
      }
      if (response && response.categoryId && activeFilter && activeFilter.categoryId) {
        setValue('icon', String(activeFilter.categoryId));
      }
      if (response && response.type && activeFilter && activeFilter.type) {
        setValue('type', activeFilter.type);
      }
      if (
        response &&
        response.refuelingTypeList &&
        activeFilter &&
        activeFilter.refuelingAreaType
      ) {
        setValue('refuelingTypeList', activeFilter.refuelingAreaType);
      }
      if (activeFilter) {
        if (activeFilter.refueling && activeFilter.waypoint) {
          setValue('areaType', 'waypointAndFuelArea');
        } else if (activeFilter.refueling && !activeFilter.waypoint) {
          setValue('areaType', 'fuelArea');
        } else if (!activeFilter.refueling && activeFilter.waypoint) {
          setValue('areaType', 'waypoint');
        }
      }

      setCustomerFormData(formDataCustomer);
      setFormOptions(response);

      setFilterOptions(normalizeOptions(response, intlShape));
    }
  };

  // React.useEffect(() => {
  //   handleFormOptions();
  // }, [handleFormOptions]);

  const { isLoading, data: _waypointOptions } = useQuery({
    queryKey: ['filterWaypointsOptions', selectedCustomers],
    queryFn: () => handleFormOptions(),
    // staleTime: 9000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleResetFilter = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    props.closeFilterOptions(event);
    setActiveFilter(null);
  };

  const handleOnSubmit = async (values: FilterWaypointsFormValues) => {
    const filters: IFiltersWaypoint = {
      categoryId: Number(values.icon),
      customerId: Number(values.customer),
    };

    if (values.refuelingTypeList) {
      filters.refuelingAreaType = values.refuelingTypeList;
    }
    if (values.type) {
      filters.type = values.type;
    }

    switch (values.areaType) {
      case 'waypoint':
        filters.waypoint = true;
        // filters.refueling = false;
        break;
      case 'fuelArea':
        filters.refueling = true;
        // filters.waypoint = false;
        break;
      case 'waypointAndFuelArea':
        filters.refueling = true;
        filters.waypoint = true;
        break;
      default:
        break;
    }

    setActiveFilter(filters);
    props.saveFilterWaypoint();
  };

  // const handleValidate = (values) => {
  //   let errors = {};

  //   if (!values.type && !values.icon && !values.customer) {
  //     errors.type = f({ id: 'alarms.modal.rule.validation.min.one.input' });
  //     errors.icon = f({ id: 'alarms.modal.rule.validation.min.one.input' });
  //     errors.customer = f({ id: 'alarms.modal.rule.validation.min.one.input' });
  //   }
  //   if (values.type || values.icon || values.customer) {
  //     errors = {};
  //   }

  //   return errors;
  // };

  const handleSubmitButton = (): boolean => {
    if (
      !watch('icon') &&
      !watch('type') &&
      !watch('customer') &&
      !watch('refuelingTypeList') &&
      !watch('areaType')
    ) {
      return true;
    }
    return false;
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      {isLoading && <CmdLoader inContent={true} />}
      <div className="b-mc-waypoints">
        <div className="b-mc-waypoints-title">
          <a href="#">
            <i className="cmd-icon cmd-icon-back mr-1" onClick={props.closeFilterOptions} />
            {f({ id: 'waypoints.form.filter.title' })}
          </a>
        </div>
        <div className="b-mc-waypoints-content h-[300px] overflow-y-auto">
          <Controller
            name="areaType"
            control={control}
            render={({ field }) => (
              <CmdSimpleSelect
                {...field}
                id="areaType"
                // name="customer"
                label={f({ id: 'enum.area.tab.title' })}
                // labelClassName="col-3 form-label"
                introOption={f({ id: 'form.choose' })}
                options={translateOptions(intlShape)}
                // rowClassName="row align-items-center"
                // columnClassName="col-9"
                searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                onChangeCallback={(value) => {
                  setValue('areaType', value);
                  trigger('areaType');
                }}
              />
            )}
          />
          {customerFormData && (
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithSearchHookForm
                  {...field}
                  id="customer"
                  name="customer"
                  label={f({ id: 'waypoints.form.customer.title' })}
                  // labelClassName="col-3 form-label"
                  introOption={f({ id: 'form.choose' })}
                  options={customerFormData}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                  control={control}
                />
              )}
            />
          )}
          {formOptions && formOptions.categoryId && (
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithIcon
                  {...field}
                  label={f({ id: 'waypoints.form.category.title' })}
                  options={formOptions.categoryId.values}
                  introOption={f({ id: 'form.choose' })}
                  control={control}
                  onChangeCallback={(value) => {
                    setValue('icon', value);
                    trigger('icon');
                  }}
                />
              )}
            />
          )}

          {formOptions && (
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  id="type"
                  name="type"
                  label={f({ id: 'waypoints.edit.type' })}
                  // labelClassName="col-3 form-label"
                  introOption={f({ id: 'form.choose' })}
                  options={[
                    ...formOptions.type.values.filter(
                      (item) =>
                        item.value === 'customer' &&
                        PermissionsService.hasAccess(
                          PERMISSION_WAYPOINT_CUSTOMER,
                          ['read'],
                          Number(watch('customer'))
                        )
                    ),
                    ...formOptions.type.values.filter(
                      (item) =>
                        item.value === 'private' &&
                        PermissionsService.hasAccess(
                          PERMISSION_WAYPOINT_PRIVATE,
                          ['read'],
                          Number(watch('customer'))
                        )
                    ),
                  ]}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  onChangeCallback={(value) => {
                    setValue('type', value);
                    trigger('type');
                  }}
                />
              )}
            />
          )}
          {formOptions?.refuelingTypeList && (
            <Controller
              name="refuelingTypeList"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  id="refuelingTypeList"
                  label={f({ id: 'enum.area.refuelingTypeList.title' })}
                  introOption={f({ id: 'form.choose' })}
                  options={formOptions.refuelingTypeList || []}
                  searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                  onChangeCallback={(value) => {
                    setValue('refuelingTypeList', value);
                    trigger('refuelingTypeList');
                  }}
                />
              )}
            />
          )}
        </div>
        <div className="b-mc-waypoints-footer">
          <div className="row">
            <div className="col">
              <a href="" className="e-button" onClick={handleResetFilter}>
                {f({ id: 'waypoints.form.filter.clear' })}
              </a>
            </div>
            <div className="col">
              <button
                type="submit"
                className={`e-button e-button--gray ${
                  handleSubmitButton() ? 'e-button--disabled' : ''
                }`}
              >
                {f({ id: 'waypoints.form.filter.apply' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default FilterWaypointsForm;
